import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea } from "recharts";
import { PHASES, PHASE_LABELS } from "@/lib/format";
import { useMediaQuery } from "@/lib/hooks";

/** Resolve a CSS custom property (HSL triplet) to a colour string recharts can use. */
export function cssHsl(name, alpha) {
  if (typeof window === "undefined") return "#000";
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!v) return "#000";
  return alpha === undefined ? `hsl(${v})` : `hsl(${v} / ${alpha})`;
}

/* ---------------------------------------------------------------- KPI tile */

/** Calm count-up for a KPI number (600ms, ease-out). Static when reduced motion is preferred. */
export function useCountUp(value, duration = 600) {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(typeof value === "number" ? 0 : value);
  const fromRef = useRef(0);
  useEffect(() => {
    if (typeof value !== "number") { setShown(value); return undefined; }
    if (reduce) { setShown(value); fromRef.current = value; return undefined; }
    const from = fromRef.current;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(from + (value - from) * e));
      if (p < 1) raf = requestAnimationFrame(tick); else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, reduce]);
  return shown;
}

/**
 * One number that matters, a short reading of it, and optionally a tiny chart.
 * Renders as a link/button when it leads somewhere.
 */
export const KpiTile = ({ label, icon: Icon, value, unit, sub, children, to, onClick, accent = false, active = false, testId }) => {
  const shown = useCountUp(value);
  const body = (
    <>
      <div className="kpi-label">
        <span>{label}</span>
        {Icon && <Icon aria-hidden="true" />}
      </div>
      <p className="kpi-n" data-testid={testId ? `${testId}-value` : undefined} aria-label={`${value}${unit ? ` ${unit}` : ""}`}>
        {shown}{unit && <small>{unit}</small>}
      </p>
      {sub && <p className="kpi-sub">{sub}</p>}
      {children}
    </>
  );
  if (to) return <Link to={to} className="kpi" data-accent={accent} data-testid={testId}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="kpi" data-accent={accent} aria-pressed={active} data-testid={testId}>{body}</button>;
  return <div className="kpi" data-accent={accent} data-testid={testId}>{body}</div>;
};

/** Proportional segments in one slim bar. segments: [{ key, label, value, color }] */
export const SegmentBar = ({ segments, testId }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <div className="segbar" role="img" aria-label={segments.filter((s) => s.value).map((s) => `${s.label} ${s.value}`).join(", ")} data-testid={testId}>
      {segments.filter((s) => s.value > 0).map((s) => (
        <span key={s.key} style={{ width: `${(s.value / total) * 100}%`, "--seg": s.color }} title={`${s.label}: ${s.value}`} />
      ))}
    </div>
  );
};

/** Tiny columns (e.g. per week). values: [{ label, value, now }] */
export const MiniColumns = ({ values, color = "var(--brand-navy)", testId }) => {
  const max = Math.max(1, ...values.map((v) => v.value));
  return (
    <div className="mini-cols" role="img" aria-label={values.map((v) => `${v.label}: ${v.value}`).join(", ")} data-testid={testId}>
      {values.map((v) => (
        <span key={v.label} style={{ height: `${Math.max(8, (v.value / max) * 100)}%`, "--seg": color, opacity: v.value === 0 ? 0.25 : 1 }} data-now={v.now} title={`${v.label}: ${v.value}`} />
      ))}
    </div>
  );
};

/* ---------------------------------------------------------------- Phase distribution */

const ACTIVE_PHASES = PHASES.filter((p) => p !== "levert");

/** Lifecycle as a horizontal bar list: where the active deliveries are right now. */
export const PhaseBars = ({ counts = {}, testId = "phase-bars" }) => {
  const max = Math.max(1, ...ACTIVE_PHASES.map((p) => counts[p] || 0));
  return (
    <ul className="space-y-0.5" data-testid={testId}>
      {ACTIVE_PHASES.map((p) => {
        const n = counts[p] || 0;
        return (
          <li key={p}>
            <Link to={`/leveranser?fase=${p}`} className="phase-row" style={{ "--seg": `var(--phase-${p})` }} data-testid={`phase-bar-${p}`} aria-label={`${PHASE_LABELS[p]}: ${n} leveranser`}>
              <span className={`text-[14px] ${n ? "text-foreground" : "text-muted-foreground"}`}>{PHASE_LABELS[p]}</span>
              <span className="flex items-center"><span className="ph-bar" style={{ width: `${(n / max) * 100}%`, opacity: n ? 1 : 0 }} /></span>
              <span className={`text-right text-[14px] tabular ${n ? "font-semibold" : "text-muted-foreground"}`}>{n}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

/* ---------------------------------------------------------------- Weekly load */

const SERIES = [
  { key: "produksjon", label: "Produksjon", varName: "--kind-produksjon" },
  { key: "montering", label: "Montering", varName: "--kind-montering" },
  { key: "service", label: "Service", varName: "--kind-service" },
  { key: "levering", label: "Avtalt levering", varName: "--kind-levering" },
];

const LoadTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  const label = payload[0]?.payload?.full;
  return (
    <div className="chart-tip" data-testid="load-tooltip">
      <p className="font-semibold">{label} <span className="font-normal text-muted-foreground">· {total} {total === 1 ? "aktivitet" : "aktiviteter"}</span></p>
      <ul className="mt-1 space-y-0.5">
        {payload.filter((p) => p.value).map((p) => (
          <li key={p.dataKey} className="flex items-center justify-between gap-4">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-[2px]" style={{ background: p.fill }} />{SERIES.find((s) => s.key === p.dataKey)?.label}</span>
            <span className="tabular font-medium">{p.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Planned activities per week (counts – not capacity). weeks: [{week,label,is_current}], items: plan items.
 */
export const WeeklyLoadChart = ({ weeks = [], items = [], height = 190, testId = "weekly-load-chart" }) => {
  const data = useMemo(() => weeks.map((w) => {
    const row = { week: w.week, label: w.label.replace("Uke ", "U"), full: w.label, now: w.is_current, produksjon: 0, montering: 0, service: 0, levering: 0 };
    for (const it of items) {
      if (it.week !== w.week) continue;
      if (it.kind === "produksjon") row.produksjon += 1;
      else if (it.kind === "montering") row.montering += 1;
      else if (it.kind === "service") row.service += 1;
      else if (it.kind === "avtalt_levering") row.levering += 1;
    }
    return row;
  }), [weeks, items]);
  const colors = useMemo(() => Object.fromEntries(SERIES.map((s) => [s.key, cssHsl(s.varName)])), []);
  const compact = useMediaQuery("(max-width: 639px)");
  const xKey = compact ? "label" : "full";
  const grid = cssHsl("--border");
  const tick = cssHsl("--muted-foreground");
  const nowColor = cssHsl("--brand-blue");
  const nowRow = data.find((r) => r.now);
  const reduce = useReducedMotion();

  return (
    <div data-testid={testId}>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 6, right: 4, left: -22, bottom: 0 }} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke={grid} strokeOpacity={0.6} strokeDasharray="0" />
            {nowRow && <ReferenceArea x1={nowRow[xKey]} x2={nowRow[xKey]} fill={nowColor} fillOpacity={0.07} stroke="none" ifOverflow="visible" />}
            <XAxis dataKey={xKey} tickLine={false} axisLine={false} tick={({ x, y, payload }) => {
              const d = data.find((r) => r[xKey] === payload.value);
              return <text x={x} y={y + 12} textAnchor="middle" fontSize={12} fontWeight={d?.now ? 600 : 400} fill={d?.now ? nowColor : tick}>{payload.value}</text>;
            }} interval={0} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: tick }} width={40} />
            <Tooltip cursor={{ fill: cssHsl("--foreground", 0.04) }} content={<LoadTip />} />
            {SERIES.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} stackId="a" fill={colors[s.key]} radius={i === SERIES.length - 1 ? [3, 3, 0, 0] : 0} maxBarSize={44} isAnimationActive={!reduce} animationDuration={320} animationEasing="ease-out" />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-muted-foreground" aria-hidden="true">
        {SERIES.map((s) => <span key={s.key} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: colors[s.key] }} />{s.label}</span>)}
      </div>
    </div>
  );
};

/** Section heading used above chart cards and lists. */
export const CardTitle = ({ title, meta, to, toLabel = "Åpne", testId }) => (
  <div className="mb-3 flex items-baseline justify-between gap-3">
    <div className="min-w-0">
      <h2 className="text-[15.5px] font-semibold leading-snug">{title}</h2>
      {meta && <p className="t-meta mt-0.5">{meta}</p>}
    </div>
    {to && <Link to={to} className="action-link !text-[13px] !font-normal text-muted-foreground" data-testid={testId}>{toLabel} <ArrowRight /></Link>}
  </div>
);
